import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { healthCheckOrderDetailsUrl } from '@app/definition/API';
import useLoadData from '@app/common/loadData/useLoadData';
import { OrderDetailsI, TestCategory, TestType } from './types';
import { NullableNumber } from '@app/common/types';

const useOrderDetails = (
  orderId: NullableNumber
): {
  loading: boolean;
  error: boolean;
  orderDetails: OrderDetailsI;
  otpExpireTime: number | null;
  setShouldReload: React.Dispatch<React.SetStateAction<boolean>>;
} => {
  const dispatch = useDispatch();
  const initialState = useMemo(
    (): OrderDetailsI => ({
      id: null,
      firstName: '',
      lastName: '',
      email: '',
      status: '',
      updatedAt: 0,
      testType: '' as TestType,
      testCategory: '' as TestCategory,
      passportNumber: '',
      birthdate: '',
      method: '',
      bookedDate: 0,
      sampleTakenAt: 0,
      reportedAt: 0,
      timerEnd: 0,
      result: null,
      referenceCode: '',
      barcodeNumber: '',
      isResendCertificate: false,
      isCertificateGenerated: false,
    }),
    []
  );
  const [details, setDetails] = useState<OrderDetailsI>(initialState);
  const [shouldReload, setShouldReload] = useState(true);
  const [otpExpireTime, setOtpExpireTime] = useState<number | null>(null);
  const { error, loading, loadData } = useLoadData<OrderDetailsI>();

  useEffect(() => {
    if (!orderId) {
      setDetails(initialState);
      return;
    }
    const fetchData = async () => {
      setShouldReload(false);
      const response = await loadData(
        healthCheckOrderDetailsUrl.replace('%id%', String(orderId))
      );
      if (!response.error) {
        setDetails(response.data);
        const otpExpireTime = response?.response?.data.otpExpireTime;
        setOtpExpireTime(otpExpireTime);
      }
    };
    if (shouldReload) {
      fetchData();
    }
  }, [dispatch, loadData, setDetails, orderId, initialState, shouldReload]);

  return {
    orderDetails: details,
    otpExpireTime,
    loading,
    error,
    setShouldReload,
  };
};

export default useOrderDetails;
